import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { PenLine, Eraser, Check } from "lucide-react";

/**
 * SignaturePad — captures a client's finger/mouse signature and returns
 * a PNG data URL via onSave. Used inline in the JobEditor sign-off dialog.
 */
const SignaturePad = ({ open, onOpenChange, defaultName = "", onSave, initialSignature = "", initialName = "" }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [name, setName] = useState(initialName || defaultName || "");
  const [empty, setEmpty] = useState(!initialSignature);

  const setup = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2; // retina crisp
    canvas.height = rect.height * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = "#050505";
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, rect.height);
    if (initialSignature) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = initialSignature;
      setEmpty(false);
    } else {
      setEmpty(true);
    }
  };

  useEffect(() => { if (open) setTimeout(setup, 20); /* eslint-disable-next-line */ }, [open]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return [clientX - rect.left, clientY - rect.top];
  };

  const start = (e) => {
    e.preventDefault();
    const [x, y] = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
    setEmpty(false);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const [x, y] = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => setDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.scale(2, 2);
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = "#050505";
    setEmpty(true);
  };

  const save = () => {
    if (empty) return;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSave({ dataUrl, name: name.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-2 border-foreground rounded-none max-w-lg" data-testid="signature-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-2xl flex items-center gap-2">
            <PenLine className="w-5 h-5" /> Client sign-off
          </DialogTitle>
          <DialogDescription>
            Hand the phone to the client. Their signature confirms the located services are as marked.
          </DialogDescription>
        </DialogHeader>

        <label className="font-heading font-bold uppercase text-xs tracking-widest">Client name</label>
        <input data-testid="sign-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Full name"
          className="w-full border-2 border-foreground bg-background px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary" />

        <label className="font-heading font-bold uppercase text-xs tracking-widest mt-2">Signature</label>
        <div className="relative border-2 border-foreground bg-white" style={{ height: 180 }}>
          <canvas
            ref={canvasRef}
            data-testid="sign-canvas"
            className="w-full h-full no-touch-action cursor-crosshair"
            style={{ display: "block", touchAction: "none" }}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          />
          {empty && (
            <div className="pointer-events-none absolute inset-0 grid place-items-center font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              Sign here
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <button data-testid="sign-clear" onClick={clear} className="btn-ghost-rugged inline-flex items-center gap-2">
            <Eraser className="w-4 h-4" /> Clear
          </button>
          <button data-testid="sign-save" onClick={save} disabled={empty || !name.trim()}
            className="btn-rugged inline-flex items-center gap-2 disabled:opacity-40">
            <Check className="w-4 h-4" /> Save sign-off
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SignaturePad;
